var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
/**
 * @file Group component
 * @author liyanan
 */
import React, { useImperativeHandle, Fragment, useEffect, useState, useCallback, } from 'react';
import classNames from 'classnames';
import MultiToneClear from 'acud-icon/lib/icons/MultiToneClear';
import Popover from '../popover';
import { ConfigContext } from '../config-provider';
import SizeContext from '../config-provider/SizeContext';
import { useLocaleReceiver } from '../locale-provider/LocaleReceiver';
import useCombinedRefs from '../_util/hooks/useCombinedRefs';
export const resolveTargetValue = (event, value) => {
    const newEvent = Object.assign(Object.assign({}, event), { target: Object.assign(Object.assign({}, event.target), { value }) });
    return newEvent;
};
const Input = React.forwardRef((props, ref) => {
    const [locale] = useLocaleReceiver('Input');
    const { prefixCls: customizePrefixCls, size: customizeSize, placeholder = locale === null || locale === void 0 ? void 0 : locale.placeholder, warning, warningPopover, warningText, tips, disabled, readOnly, defaultValue, limitLength, label, prefix, suffix, addonBefore, addonAfter, className, allowClear, onPressEnter, onKeyDown, handleClearAll, onCompositionStart, onCompositionEnd, onPaste, type, style, hidden = false, forbidIfLimit = false } = props, rest = __rest(props, ["prefixCls", "size", "placeholder", "warning", "warningPopover", "warningText", "tips", "disabled", "readOnly", "defaultValue", "limitLength", "label", "prefix", "suffix", "addonBefore", "addonAfter", "className", "allowClear", "onPressEnter", "onKeyDown", "handleClearAll", "onCompositionStart", "onCompositionEnd", "onPaste", "type", "style", "hidden", "forbidIfLimit"]);
    const [value, setValue] = useState(props.value || defaultValue || '');
    const [clearIconColor, setClearIconColor] = useState('#D4D6D9');
    const [showClearIcon, setShowClearIcon] = useState(false);
    const [focusClass, setFocusClass] = useState('');
    // const userTypingRef = React.useRef(false);
    const composedRef = React.useRef(false);
    const [composingValue, setComposingValue] = useState('');
    const innerRef = React.useRef(null);
    const combinedRef = useCombinedRefs(ref, innerRef);
    useImperativeHandle(ref, () => ({
        blur: () => {
            setFocusClass('');
        },
        focus: () => {
            combinedRef.current.focus();
        },
    }));
    useEffect(() => {
        if ('value' in props && value !== props.value) {
            setComposingValue(props.value || '');
            setValue(props.value || '');
            if (props.value === 0) {
                setComposingValue(0);
                setValue(0);
            }
        }
        if ('label' in props && props.label) {
            if (props.allowClear) {
                setShowClearIcon(true);
            }
            else {
                setShowClearIcon(false);
            }
        }
        if (!disabled && !readOnly) {
            if (value.length && props.allowClear) {
                setShowClearIcon(true);
            }
        }
        if (props.allowClear && !value.length) {
            setShowClearIcon(false);
        }
    }, [props, value, disabled, readOnly]);
    const handleFocus = (e) => {
        if (disabled || readOnly) {
            return;
        }
        e.stopPropagation();
        setFocusClass('focus');
        if (props.onFocus) {
            props.onFocus(e);
        }
    };
    const handleBlur = (e) => {
        if (disabled || readOnly) {
            return;
        }
        setFocusClass('');
        if (props.onBlur) {
            props.onBlur(e);
        }
    };
    const handleOnKeyDown = (e) => {
        // userTypingRef.current = true;
        if (onPressEnter && e.key === 'Enter') {
            if (composedRef.current) {
                return;
            }
            onPressEnter(e);
        }
        if (onKeyDown) {
            onKeyDown(e);
        }
    };
    const handleOnChange = (e) => {
        var _a;
        if (composedRef.current) {
            setComposingValue(e.target.value);
        }
        else {
            if (forbidIfLimit
                && limitLength
                && e.target.value.length >= limitLength) {
                setComposingValue(e.target.value.slice(0, limitLength));
                setValue(e.target.value.slice(0, limitLength));
                e.target.value = e.target.value.slice(0, limitLength);
            }
            else {
                setComposingValue(e.target.value);
                setValue(e.target.value);
            }
            if (!e.target.value.length) {
                setShowClearIcon(false);
            }
            (_a = props.onChange) === null || _a === void 0 ? void 0 : _a.call(props, e);
        }
    };
    const handleOuterFocus = () => {
        setFocusClass('focus');
    };
    const handleOuterBlur = () => {
        setFocusClass('');
    };
    const handleMouseDownClear = useCallback(e => {
        e.preventDefault();
    }, []);
    const clickClear = e => {
        if (allowClear) {
            setShowClearIcon(false);
            // setValue('');
            // 强制转换一下类型
            handleOnChange(resolveTargetValue(e, ''));
        }
        if (label) {
            setShowClearIcon(false);
        }
        if (handleClearAll) {
            handleClearAll();
        }
        // console.log('combinedRef', combinedRef);
        // combinedRef.current.focus();
    };
    const hoverClear = () => {
        setClearIconColor('#84868C');
    };
    const clearMouseLeave = () => {
        setClearIconColor('#D4D6D9');
    };
    const onHandlePaste = e => {
        if (onPaste) {
            onPaste(e);
        }
    };
    const { getPrefixCls } = React.useContext(ConfigContext);
    const prefixCls = getPrefixCls('input', customizePrefixCls);
    const renderPrefix = () => {
        if (prefix) {
            return (<span className={`${prefixCls}-prefix`} aria-disabled={disabled || readOnly}>
                    {prefix}
                </span>);
        }
        return null;
    };
    const renderSuffix = () => {
        if (suffix || limitLength) {
            return (<span className={`${prefixCls}-suffix`} aria-disabled={disabled || readOnly}>
                    {suffix}
                    {(!disabled || !readOnly) && limitLength && (<Fragment>
                            <span className={`${prefixCls}-suffix-limit-text`}>
                                <span className={classNames({
                        [`${prefixCls}-suffix-warning`]: value && value.length > limitLength,
                    })}>
                                    {value && value.length ? value.length : 0}/
                                    {limitLength}
                                </span>
                            </span>
                        </Fragment>)}
                </span>);
        }
        return null;
    };
    const addonClasses = (addon) => {
        return classNames(`${prefixCls}-addon`, {
            [`${prefixCls}-addonOnlyText`]: typeof addon === 'string',
        });
    };
    const renderAddonBefore = () => {
        if (addonBefore) {
            return (<span className={addonClasses(addonBefore)} aria-disabled={disabled || readOnly}>
                    {addonBefore}
                </span>);
        }
        return null;
    };
    const renderAddonAffer = () => {
        if (addonAfter) {
            return (<span className={addonClasses(addonAfter)} aria-disabled={disabled || readOnly}>
                    {addonAfter}
                </span>);
        }
        return null;
    };
    const handleOnCompositionStart = e => {
        composedRef.current = true;
        onCompositionStart === null || onCompositionStart === void 0 ? void 0 : onCompositionStart(e);
    };
    const handleOnCompositionEnd = e => {
        if (composedRef.current) {
            composedRef.current = false;
            handleOnChange(e);
        }
        onCompositionEnd === null || onCompositionEnd === void 0 ? void 0 : onCompositionEnd(e);
    };
    const innerValue = composedRef.current ? composingValue : value !== null && value !== void 0 ? value : '';
    const size = React.useContext(SizeContext) || customizeSize;
    const classes = classNames(prefixCls, className, {
        [`${prefixCls}-sm`]: size === 'small',
        [`${prefixCls}-lg`]: size === 'large',
        [`${prefixCls}-warning`]: warning || warningPopover || (value && value.length > limitLength),
        [`${prefixCls}-has-prefix`]: prefix,
        [`${prefixCls}-has-suffix`]: suffix,
        [`${prefixCls}-has-addon-before`]: addonBefore,
        [`${prefixCls}-has-addon-after`]: addonAfter,
        [`${prefixCls}-label-input`]: label,
        [`${prefixCls}-span-focus`]: focusClass,
        [`${prefixCls}-real-password`]: type === 'password',
        [`${prefixCls}-has-clear`]: (!disabled || !readOnly) && allowClear && !label,
        [`${prefixCls}-has-limit`]: limitLength,
    });
    const inputDom = (<input {...rest} type={type} ref={combinedRef} placeholder={placeholder} value={innerValue} disabled={disabled} readOnly={readOnly} onFocus={handleFocus} onBlur={handleBlur} onKeyDown={handleOnKeyDown} onChange={handleOnChange} onCompositionStart={handleOnCompositionStart} onCompositionEnd={handleOnCompositionEnd} onPaste={onHandlePaste}/>);
    const renderInner = () => {
        return (<span className={classes} style={style} aria-disabled={disabled || readOnly} onFocus={handleOuterFocus} onBlur={handleOuterBlur}>
                {label}
                {renderAddonBefore()}
                {renderPrefix()}
                {React.cloneElement(inputDom, {
                value: innerValue,
            })}
                {(!disabled || !readOnly) && showClearIcon && (<div className={`${prefixCls}-clear-wrapper`} onClick={clickClear} onMouseEnter={hoverClear} onMouseLeave={clearMouseLeave} onMouseDown={handleMouseDownClear}>
                        <MultiToneClear width={16} color={clearIconColor}/>
                    </div>)}
                {renderSuffix()}
                {renderAddonAffer()}
            </span>);
    };
    if (!hidden) {
        const warningTextContent = (!disabled || !readOnly) && !warningPopover && warningText && (<div className={`${prefixCls}-warning-text`}>
                {warningText}
            </div>);
        const tipsContent = (!disabled || !readOnly) && !warningPopover && !warningText && tips && (<div className={`${prefixCls}-tips`}>{tips}</div>);
        return warningText ? (<Popover content={<span className={`${prefixCls}-warning-popover-text`}>
                        {warningText}
                    </span>} placement="bottomLeft" {...warningPopover && !!warningText ? {} : { visible: false }}>
                {renderInner()}
                {warningTextContent}
                {tipsContent}
            </Popover>) : (<Fragment>
                {renderInner()}
                {warningTextContent}
                {tipsContent}
            </Fragment>);
    }
    return null;
});
export default Input;
